import { useEffect, useRef, useState } from "react";
import ProjectHistory from "../ProjectHistory";
import FormatTime from "../../utils/FormatTime";
import ParseTime from "../../utils/ParseTime";
import ring from "../../assets/ring2.mp3";
import { AnimatePresence, motion } from "framer-motion";

const Project = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isStart, setIsStart] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [time, setTime] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyDatabseId, setHistoryDatabseId] = useState("");
  const [totalTime, setTotalTime] = useState(
    ParseTime(item.properties.Time.rich_text[0].plain_text)
  );
  const [percentLine, setPercentLine] = useState(
    (ParseTime(item.properties.Time.rich_text[0].plain_text) /
      ParseTime(item.properties.available_time.rich_text[0].plain_text)) * 100
  );

  const ringtoneIntervalRef = useRef(null);
  const audioRef = useRef(null);

  const COOKIE_NAME = `tm_timer_${item.id}`;

  const saveTimerToCookie = (data) => {
    try {
      const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
        JSON.stringify(data)
      )}; expires=${expires}; path=/`;
    } catch (e) {
      console.error("Ошибка сохранения таймера в cookie:", e);
    }
  };

  const readTimerFromCookie = () => {
    if (typeof document === "undefined") return null;
    try {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${COOKIE_NAME}=`);
      if (parts.length === 2) {
        const cookieValue = parts.pop().split(";").shift();
        return JSON.parse(decodeURIComponent(cookieValue));
      }
    } catch (e) {
      console.error("Ошибка чтения таймера из cookie:", e);
    }
    return null;
  };

  const clearTimerCookie = () => {
    try {
      document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    } catch (e) {
      console.error("Ошибка удаления таймера из cookie:", e);
    }
  };

  const handleUpdateDatabase = async () => {
    await fetch("https://time-api.web-hub.online/api/update-time/", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        page_id: item.id,
        new_time: FormatTime(totalTime + time),
      }),
    });
  };

  const handleUpdateHistory = async () => {
    await fetch("https://time-api.web-hub.online/api/project-history/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        date: new Date().toLocaleDateString(),
        time_spent: FormatTime(time),
        database_id: historyDatabseId,
      }),
    });
  };

  const handleStart = () => {
    setIsStart(true);
    setIsPaused(false);

    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
    }

    ringtoneIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((e) => {
          console.log("Ошибка воспроизведения:", e);
        });
      }
    }, 30 * 60 * 1000);
  };

  const handlePause = () => {
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setIsStart(false);
    setIsPaused(true);
  };

  const handleStop = async () => {
    if (ringtoneIntervalRef.current) {
      clearInterval(ringtoneIntervalRef.current);
      ringtoneIntervalRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setHistory([
      { id: new Date().getTime(), time, date: new Date().toLocaleDateString() },
      ...history,
    ]);

    setPercentLine((prev) => {
      const newTotalTime = totalTime + time;
      const newPercent = (newTotalTime / ParseTime(item.properties.available_time.rich_text[0].plain_text)) * 100;
      return newPercent > 100 ? 100 : newPercent;
    }); 

    setTotalTime((prev) => prev + time);
    setTime(0);
    setIsStart(false);
    setIsPaused(false);
    startTimeRef.current = null;

    handleUpdateDatabase();
    handleUpdateHistory();

    clearTimerCookie();
  };

  const startTimeRef = useRef(null);

  // get children
  useEffect(() => {
    async function fetchChildren() {
      const res = await fetch(
        "https://time-api.web-hub.online/api/page-children?page_id=" + item.id
      );

      const data = await res.json();
      const historyDatabaseId = data.children?.filter(
        (item) => item.type === "child_database"
      )?.[0]?.id;

      setHistoryDatabseId(historyDatabaseId);
    }

    fetchChildren();
  }, []);

  useEffect(() => {
    let interval;

    if (isStart) {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now() - time * 1000;
      }

      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - startTimeRef.current;
        const seconds = Math.floor(elapsed / 1000);
        setTime(seconds);

        setPercentLine(() => {
          const newTotalTime = totalTime + seconds;
          const newPercent = (newTotalTime / ParseTime(item.properties.available_time.rich_text[0].plain_text)) * 100;
          return newPercent > 100 ? 100 : newPercent;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isStart]);

  useEffect(() => {
    audioRef.current = new Audio(ring);
    // (опционально) Разрешаем повторное воспроизведение
    audioRef.current.load();
    return () => {
      // Очищаем при размонтировании
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (ringtoneIntervalRef.current) {
        clearInterval(ringtoneIntervalRef.current);
      }
    };
  }, []);

  // Восстановление таймера из cookie при монтировании
  useEffect(() => {
    const saved = readTimerFromCookie();
    if (!saved) return;

    const availableSeconds = ParseTime(
      item.properties.available_time.rich_text[0].plain_text
    );

    let restoredTime = saved.time || 0;
    let restoredTotalTime = saved.totalTime ?? totalTime;

    if (saved.status === "running" && saved.startTimestamp) {
      const now = Date.now();
      const elapsed = Math.max(
        0,
        Math.floor((now - saved.startTimestamp) / 1000)
      );
      restoredTime = elapsed;
    }

    const newTotalTime = restoredTotalTime + restoredTime;
    const newPercent = Math.min(
      100,
      (newTotalTime / availableSeconds) * 100
    );

    setTime(restoredTime);
    setTotalTime(restoredTotalTime);
    setPercentLine(newPercent);

    if (saved.status === "running") {
      startTimeRef.current =
        saved.startTimestamp || Date.now() - restoredTime * 1000;
      setIsStart(true);
      setIsPaused(false);
    } else if (saved.status === "paused") {
      setIsStart(false);
      setIsPaused(true);
    }
  }, []);

  // Сохранение состояния таймера в cookie
  useEffect(() => {
    const status = isStart ? "running" : isPaused ? "paused" : "stopped";
    if (status === "stopped") return;

    saveTimerToCookie({
      time,
      totalTime,
      percentLine,
      status,
      startTimestamp: startTimeRef.current,
    });
  }, [time, totalTime, percentLine, isStart, isPaused]);

  useEffect(() => {
    if (isStart) {
      document.title = `⏱ ${FormatTime(time, false)} — ${
        item.properties.Name.title[0].plain_text
      }`;
    } else {
      document.title = item.properties.Name.title[0].plain_text;
    }
  }, [isStart, time, item.properties.Name.title]);

  return (
    <>
      <div className="grid grid-cols-[1fr_1fr_3fr_1fr] mb-2 bg-white py-4 items-center px-7 rounded-2xl shadow-md">
        <div className="flex items-center gap-2">
          <svg
            width="23"
            height="20"
            viewBox="0 0 23 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 6.20182V14.5011C1 15.6943 1.47838 16.8386 2.32989 17.6823C3.18141 18.526 4.33631 19 5.54054 19H17.4595C18.6637 19 19.8186 18.526 20.6701 17.6823C21.5216 16.8386 22 15.6943 22 14.5011V8.10147C22 6.9083 21.5216 5.76399 20.6701 4.92029C19.8186 4.07659 18.6637 3.6026 17.4595 3.6026H11.5M1 6.20182V4.37416C1 3.47927 1.35878 2.62104 1.99742 1.98827C2.63606 1.35549 3.50224 1 4.40541 1H7.6973C8.06999 0.999931 8.43905 1.0726 8.7834 1.21386C9.12775 1.35513 9.44065 1.56221 9.70422 1.82329L11.5 3.60372L9.70876 5.37853C9.44519 5.63961 9.13229 5.8467 8.78794 5.98796C8.44359 6.12922 8.07453 6.20189 7.70184 6.20182H1Z"
              stroke="#5489DA"
              strokeWidth="1.5"
            />
          </svg>
          <span>{item.properties.Name.title[0].plain_text}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleStart}
            disabled={isStart}
            className="bg-[#DBF8DA] transition-all w-11 h-11 flex justify-center items-center rounded-full text-white hover:bg-[#30AA5F] group active:bg-green-700 disabled:bg-[#EBEBEB] disabled:cursor-not-allowed"
          >
            <svg
              width="13"
              height="12"
              viewBox="0 0 13 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="group-hover:fill-white fill-[#30AA5F] transition-all group-disabled:fill-[#A1A1A1]"
                d="M4.4979 0.319846C2.7993 -0.583329 0.666626 0.53356 0.666626 2.3269V9.6731C0.666626 11.4664 2.7993 12.5833 4.4979 11.6802L11.4034 8.00613C13.0877 7.11038 13.0877 4.88961 11.4034 3.99387L4.4979 0.319846Z"
              />
            </svg>
          </button>
          {/* <button
            onClick={handlePause}
            disabled={!isStart}
            className="bg-[#FFECC4] text-white group w-11 h-11 flex justify-center items-center rounded-full transition-all hover:bg-[#F29D38] active:bg-[#C97C21] disabled:bg-[#EBEBEB] disabled:cursor-not-allowed"
          >
            <svg
              width="13"
              height="12"
              viewBox="0 0 13 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="group-hover:fill-white transition-all fill-[#F29D38] group-disabled:fill-[#A1A1A1]"
                d="M3.16663 1C2.61435 1 2.16663 1.44772 2.16663 2V10C2.16663 10.5523 2.61435 11 3.16663 11H4.16663C4.71891 11 5.16663 10.5523 5.16663 10V2C5.16663 1.44772 4.71891 1 4.16663 1H3.16663ZM8.83329 1C8.28099 1 7.83329 1.44772 7.83329 2V10C7.83329 10.5523 8.28099 11 8.83329 11H9.83329C10.3856 11 10.8333 10.5523 10.8333 10V2C10.8333 1.44772 10.3856 1 9.83329 1H8.83329Z"
              />
            </svg>
          </button> */}
          <button
            onClick={handleStop}
            disabled={!isStart}
            className="bg-[#FFC4CC] text-white group w-11 h-11 flex justify-center items-center rounded-full transition-all hover:bg-[#C31919] active:bg-red-700 disabled:bg-[#EBEBEB] disabled:cursor-not-allowed"
          >
            <svg
              width="13"
              height="12"
              viewBox="0 0 13 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                className="group-hover:fill-white transition-all fill-[#C31919] group-disabled:fill-[#A1A1A1]"
                d="M0.666626 4.8009C0.666626 3.12216 0.666626 2.28279 0.993381 1.63827C1.28217 1.07436 1.74099 0.615547 2.3049 0.326755C2.94642 -8.934e-08 3.78878 0 5.46753 0H7.86573C9.54447 0 10.3838 -8.934e-08 11.0284 0.326755C11.5927 0.614539 12.0476 1.07245 12.3399 1.63827C12.6666 2.27979 12.6666 3.12216 12.6666 4.8009V7.1991C12.6666 8.87784 12.6666 9.71721 12.3399 10.3617C12.0511 10.9256 11.5923 11.3845 11.0284 11.6732C10.3868 12 9.54447 12 7.86573 12H5.46753C3.78878 12 2.94941 12 2.3049 11.6732C1.74099 11.3845 1.28217 10.9256 0.993381 10.3617C0.666626 9.72021 0.666626 8.87784 0.666626 7.1991V4.8009Z"
              />
            </svg>
          </button>
        </div>
        <div className="w-fit">
          <div className="flex items-center gap-5">
            <div className={"min-w-[115px] px-5 py-2 rounded-2xl" + (percentLine >= 90 ? " bg-[#FFC4CC33]/20" : " bg-[#F7F9FC]")}>
              <span className={"text-xs block" + (percentLine >= 90 ? " text-[#C31919]" : " text-[#627491]")}>Current time</span>
              <time className={"font-semibold text-base mt-1" + (percentLine >= 90 ? " text-[#C31919]" : " text-[#1A1C1F]")}>
                {FormatTime(time)}
              </time>
            </div>
            <div className={"min-w-[115px] px-5 py-2 rounded-2xl" + (percentLine >= 90 ? " bg-[#FFC4CC33]/20" : " bg-[#F7F9FC]")}>
              <span className={"text-xs block" + (percentLine >= 90 ? " text-[#C31919]" : " text-[#627491]")}>Total time</span>
              <time className={"font-semibold text-base mt-1" + (percentLine >= 90 ? " text-[#C31919]" : " text-[#1A1C1F]")}>
                {FormatTime(totalTime)}
              </time>
            </div>
            <div className={"min-w-[115px] px-5 py-2 rounded-2xl" + (percentLine >= 90 ? " bg-[#FFC4CC33]/20" : " bg-[#F7F9FC]")}>
              <span className={"text-xs block" + (percentLine >= 90 ? " text-[#C31919]" : " text-[#627491]")}>Avaliable time</span>
              <time className={"font-semibold text-base mt-1" + (percentLine >= 90 ? " text-[#C31919]" : " text-[#1A1C1F]")}>
                {item.properties.available_time.rich_text[0].plain_text}
              </time>
            </div>
          </div>
          <div className="h-1 w-full mt-2 rounded-3xl bg-[#EBEEF2]">
            <div className={`h-full relative` + (percentLine >= 90 ? " bg-[#C31919]" : " bg-[#455BD4]")} style={{ width: `${percentLine || 0}%` }}>
              <div className={"w-2 h-2 absolute -top-[2px] rounded-full -right-[2px]" + (percentLine >= 90 ? " bg-[#C31919]" : " bg-[#455BD4]")}></div>
            </div>
          </div>
        </div>
        <div>
          <button onClick={() => setIsOpen(!isOpen)} className="button-blue">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 5.66699V9.00033L11.0833 11.0837"
                stroke="white"
                strokeWidth="1.5"
              />
              <path
                d="M2.615 4.72616L1.99 4.72866C1.99066 4.89341 2.05634 5.05124 2.17276 5.16781C2.28918 5.28439 2.44692 5.35028 2.61167 5.35116L2.615 4.72616ZM4.73333 5.36116C4.81541 5.36154 4.89676 5.34575 4.97273 5.3147C5.04871 5.28364 5.11782 5.23793 5.17613 5.18016C5.23444 5.1224 5.2808 5.05371 5.31256 4.97803C5.34432 4.90235 5.36087 4.82115 5.36125 4.73907C5.36163 4.657 5.34585 4.57565 5.31479 4.49968C5.28374 4.4237 5.23802 4.35459 5.18025 4.29628C5.12249 4.23797 5.0538 4.19161 4.97812 4.15985C4.90244 4.12809 4.82124 4.11154 4.73917 4.11116L4.73333 5.36116ZM3.22917 2.60116C3.22828 2.4354 3.16159 2.27678 3.04375 2.16019C2.92592 2.04361 2.76659 1.97861 2.60083 1.97949C2.43507 1.98038 2.27645 2.04707 2.15987 2.16491C2.04328 2.28274 1.97828 2.44206 1.97917 2.60782L3.22917 2.60116ZM2.125 9.00032C2.125 8.83456 2.05915 8.67559 1.94194 8.55838C1.82473 8.44117 1.66576 8.37532 1.5 8.37532C1.33424 8.37532 1.17527 8.44117 1.05806 8.55838C0.940848 8.67559 0.875 8.83456 0.875 9.00032H2.125ZM13.0625 16.0378C13.1366 15.9984 13.2019 15.9444 13.2547 15.8792C13.3074 15.814 13.3465 15.7388 13.3696 15.6581C13.3927 15.5775 13.3993 15.493 13.389 15.4097C13.3788 15.3265 13.3519 15.2461 13.3099 15.1735C13.2679 15.1008 13.2117 15.0374 13.1447 14.9869C13.0777 14.9364 13.0012 14.9 12.9198 14.8797C12.8384 14.8594 12.7537 14.8557 12.6709 14.8689C12.588 14.882 12.5086 14.9117 12.4375 14.9562L13.0625 16.0378ZM14.9558 12.4378C14.9114 12.509 14.8817 12.5883 14.8686 12.6712C14.8554 12.7541 14.8591 12.8387 14.8794 12.9201C14.8997 13.0015 14.9361 13.078 14.9866 13.1451C15.0371 13.2121 15.1005 13.2682 15.1732 13.3102C15.2458 13.3522 15.3261 13.3791 15.4094 13.3894C15.4927 13.3996 15.5772 13.393 15.6578 13.3699C15.7385 13.3468 15.8137 13.3078 15.8789 13.255C15.9441 13.2022 15.9981 13.1369 16.0375 13.0628L14.9558 12.4378ZM3.28333 3.22532C3.16553 3.34202 3.09891 3.50073 3.09813 3.66654C3.09735 3.83236 3.16247 3.99169 3.27917 4.10949C3.39586 4.22729 3.55457 4.29391 3.72039 4.29469C3.8862 4.29547 4.04553 4.23035 4.16333 4.11366L3.28333 3.22532ZM14.7183 3.28116C11.535 0.0978246 6.39083 0.0661576 3.22833 3.22866L4.11167 4.11116C6.77833 1.44532 11.1308 1.46116 13.835 4.16532L14.7183 3.28116ZM3.22833 3.22866L2.17333 4.28366L3.05667 5.16699L4.1125 4.11282L3.22833 3.22866ZM2.61167 5.35116L4.73333 5.36116L4.73917 4.11116L2.61833 4.10116L2.61167 5.35116ZM3.24 4.72282L3.22917 2.60116L1.97917 2.60782L1.99 4.72866L3.24 4.72282ZM9 2.12532C10.8234 2.12532 12.572 2.84965 13.8614 4.13897C15.1507 5.42828 15.875 7.17696 15.875 9.00032H17.125C17.125 6.84544 16.269 4.77882 14.7452 3.25508C13.2215 1.73135 11.1549 0.875324 9 0.875324V2.12532ZM9 15.8753C7.17664 15.8753 5.42795 15.151 4.13864 13.8617C2.84933 12.5724 2.125 10.8237 2.125 9.00032H0.875C0.875 11.1552 1.73102 13.2218 3.25476 14.7456C4.77849 16.2693 6.84512 17.1253 9 17.1253V15.8753ZM12.4375 14.9562C11.3929 15.5607 10.2069 15.8779 9 15.8753V17.1253C10.4792 17.1253 11.8667 16.7295 13.0625 16.0378L12.4375 14.9562ZM15.875 9.00032C15.8776 10.2072 15.5604 11.3933 14.9558 12.4378L16.0375 13.0628C16.7521 11.8283 17.1273 10.4267 17.125 9.00032H15.875ZM4.16333 4.11366C5.44904 2.83704 7.18815 2.12263 9 2.12532V0.875324C6.85862 0.872475 4.80323 1.71687 3.28333 3.22532L4.16333 4.11366Z"
                fill="white"
              />
            </svg>
            <span>Open History</span>
          </button>
        </div>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-5 overflow-hidden"
            >
              <ProjectHistory
                setHistory={setHistory}
                history={history}
                historyDatabseId={historyDatabseId}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Project;
